import {
  memberGetOrder as publicMemberGetOrder,
  memberListOrders as publicMemberListOrders,
  requestCancellation as publicRequestCancellation,
  createOnlineOrder as publicCreateOnlineOrder,
  createOfflineOrder as publicCreateOfflineOrder,
  getOnlineOrderPreview as publicGetOnlineOrderPreview,
  getOfflineOrderPreview as publicGetOfflineOrderPreview,
  getPricePreview as publicGetPricePreview,
  managementGetOrder as publicManagementGetOrder,
  managementListOrders as publicManagementListOrders,
  postponeEndDate as publicPostponeEndDate,
  cancelOrder as publicCancelOrder,
  markAsPaid as publicMarkAsPaid,
  pauseOrder as publicPauseOrder,
  resumeOrder as publicResumeOrder,
} from './pricing-plans-v2-order-orders.public.js';
import { createRESTModule } from '@wix/sdk-runtime/rest-modules';
import { createEventModule } from '@wix/sdk-runtime/event-definition-modules';
import {
  BuildRESTFunction,
  MaybeContext,
  BuildEventDefinition,
} from '@wix/sdk-types';
import { onOrderCanceled as publicOnOrderCanceled } from './pricing-plans-v2-order-orders.public.js';
import { onOrderCreated as publicOnOrderCreated } from './pricing-plans-v2-order-orders.public.js';
import { onOrderStartDateChanged as publicOnOrderStartDateChanged } from './pricing-plans-v2-order-orders.public.js';
import { onOrderUpdated as publicOnOrderUpdated } from './pricing-plans-v2-order-orders.public.js';
import { onOrderAutoRenewCanceled as publicOnOrderAutoRenewCanceled } from './pricing-plans-v2-order-orders.public.js';
import { onOrderCycleStarted as publicOnOrderCycleStarted } from './pricing-plans-v2-order-orders.public.js';
import { onOrderEndDatePostponed as publicOnOrderEndDatePostponed } from './pricing-plans-v2-order-orders.public.js';
import { onOrderEnded as publicOnOrderEnded } from './pricing-plans-v2-order-orders.public.js';
import { onOrderMarkedAsPaid as publicOnOrderMarkedAsPaid } from './pricing-plans-v2-order-orders.public.js';
import { onOrderPaused as publicOnOrderPaused } from './pricing-plans-v2-order-orders.public.js';
import { onOrderPurchased as publicOnOrderPurchased } from './pricing-plans-v2-order-orders.public.js';
import { onOrderResumed as publicOnOrderResumed } from './pricing-plans-v2-order-orders.public.js';
import { onOrderStarted as publicOnOrderStarted } from './pricing-plans-v2-order-orders.public.js';

export const memberGetOrder: MaybeContext<
  BuildRESTFunction<typeof publicMemberGetOrder> & typeof publicMemberGetOrder
> = /*#__PURE__*/ createRESTModule(publicMemberGetOrder);
export const memberListOrders: MaybeContext<
  BuildRESTFunction<typeof publicMemberListOrders> &
    typeof publicMemberListOrders
> = /*#__PURE__*/ createRESTModule(publicMemberListOrders);
export const requestCancellation: MaybeContext<
  BuildRESTFunction<typeof publicRequestCancellation> &
    typeof publicRequestCancellation
> = /*#__PURE__*/ createRESTModule(publicRequestCancellation);
export const createOnlineOrder: MaybeContext<
  BuildRESTFunction<typeof publicCreateOnlineOrder> &
    typeof publicCreateOnlineOrder
> = /*#__PURE__*/ createRESTModule(publicCreateOnlineOrder);
export const createOfflineOrder: MaybeContext<
  BuildRESTFunction<typeof publicCreateOfflineOrder> &
    typeof publicCreateOfflineOrder
> = /*#__PURE__*/ createRESTModule(publicCreateOfflineOrder);
export const getOnlineOrderPreview: MaybeContext<
  BuildRESTFunction<typeof publicGetOnlineOrderPreview> &
    typeof publicGetOnlineOrderPreview
> = /*#__PURE__*/ createRESTModule(publicGetOnlineOrderPreview);
export const getOfflineOrderPreview: MaybeContext<
  BuildRESTFunction<typeof publicGetOfflineOrderPreview> &
    typeof publicGetOfflineOrderPreview
> = /*#__PURE__*/ createRESTModule(publicGetOfflineOrderPreview);
export const getPricePreview: MaybeContext<
  BuildRESTFunction<typeof publicGetPricePreview> & typeof publicGetPricePreview
> = /*#__PURE__*/ createRESTModule(publicGetPricePreview);
export const managementGetOrder: MaybeContext<
  BuildRESTFunction<typeof publicManagementGetOrder> &
    typeof publicManagementGetOrder
> = /*#__PURE__*/ createRESTModule(publicManagementGetOrder);
export const managementListOrders: MaybeContext<
  BuildRESTFunction<typeof publicManagementListOrders> &
    typeof publicManagementListOrders
> = /*#__PURE__*/ createRESTModule(publicManagementListOrders);
export const postponeEndDate: MaybeContext<
  BuildRESTFunction<typeof publicPostponeEndDate> & typeof publicPostponeEndDate
> = /*#__PURE__*/ createRESTModule(publicPostponeEndDate);
export const cancelOrder: MaybeContext<
  BuildRESTFunction<typeof publicCancelOrder> & typeof publicCancelOrder
> = /*#__PURE__*/ createRESTModule(publicCancelOrder);
export const markAsPaid: MaybeContext<
  BuildRESTFunction<typeof publicMarkAsPaid> & typeof publicMarkAsPaid
> = /*#__PURE__*/ createRESTModule(publicMarkAsPaid);
export const pauseOrder: MaybeContext<
  BuildRESTFunction<typeof publicPauseOrder> & typeof publicPauseOrder
> = /*#__PURE__*/ createRESTModule(publicPauseOrder);
export const resumeOrder: MaybeContext<
  BuildRESTFunction<typeof publicResumeOrder> & typeof publicResumeOrder
> = /*#__PURE__*/ createRESTModule(publicResumeOrder);
/**
 * Triggered when an order is canceled.
 *
 * This webhook is triggered either immediately or at the end of the current payment cycle, as follows:
 * + If the order is canceled and `effectiveAt` is set to `IMMEDIATELY`, the webhook is triggered immediately when canceled.
 * + If the order is canceled and `effectiveAt` is set to `NEXT_PAYMENT_DATE`, the webhook is triggered at the end of the current payment cycle. In this case, the Order Auto Renew Canceled Webhook is triggered immediately.
 */
export const onOrderCanceled: BuildEventDefinition<
  typeof publicOnOrderCanceled
> &
  typeof publicOnOrderCanceled = createEventModule(publicOnOrderCanceled);
/**
 * Triggered when an order is created.
 */
export const onOrderCreated: BuildEventDefinition<typeof publicOnOrderCreated> &
  typeof publicOnOrderCreated = createEventModule(publicOnOrderCreated);
/**
 * Triggered when an order's `startDate` is changed.
 */
export const onOrderStartDateChanged: BuildEventDefinition<
  typeof publicOnOrderStartDateChanged
> &
  typeof publicOnOrderStartDateChanged = createEventModule(
  publicOnOrderStartDateChanged
);
/**
 * Triggered for any of the following update events:
 *
 * + Order is paid for. Order Purchased is also triggered.
 * + Order reaches its start date or end date. Order Started and Order Ended, respectively, are also triggered.
 * + New payment cycle of an order starts. Order Cycle Started is also triggered.
 * + Offline order is marked as paid. Order Marked As Paid is also triggered.
 * + End date of the order is postponed. Order End Date Postponed is also triggered
 * + Order is paused, or a paused order is resumed. Order Paused and Order Resumed, respectively, are also triggered.
 * + Order is canceled, either immediately or at the end of the payment cycle. Order Canceled and Order Auto Renew Canceled, respectively, are also triggered.
 */
export const onOrderUpdated: BuildEventDefinition<typeof publicOnOrderUpdated> &
  typeof publicOnOrderUpdated = createEventModule(publicOnOrderUpdated);
/**
 * Triggered when an order is canceled and `effectiveAt` is set to `NEXT_PAYMENT_DATE`.
 *
 * This webhook is *not* triggered in the following scenarios:
 * + When an order is canceled and `effectiveAt` is set to `IMMEDIATELY`. Instead, at the time of cancellation, Order Canceled is triggered.
 * + When an order expires at the end of the current payment cycle because it was canceled and `effectiveAt` was set to `NEXT_PAYMENT_DATE`. Instead, at the time of expiration, Order Canceled and Order Ended are triggered.
 */
export const onOrderAutoRenewCanceled: BuildEventDefinition<
  typeof publicOnOrderAutoRenewCanceled
> &
  typeof publicOnOrderAutoRenewCanceled = createEventModule(
  publicOnOrderAutoRenewCanceled
);
/**
 * Triggered at the start of a new payment cycle for an existing order.
 *
 * Not triggered at the initial start of an offline order.
 */
export const onOrderCycleStarted: BuildEventDefinition<
  typeof publicOnOrderCycleStarted
> &
  typeof publicOnOrderCycleStarted = createEventModule(
  publicOnOrderCycleStarted
);
/**
 * Triggered when an order's `endDate` is postponed.
 */
export const onOrderEndDatePostponed: BuildEventDefinition<
  typeof publicOnOrderEndDatePostponed
> &
  typeof publicOnOrderEndDatePostponed = createEventModule(
  publicOnOrderEndDatePostponed
);
/**
 * Triggered when an order ends.
 *
 * This webhook is triggered:
 * + When an order expires at the end of the current payment cycle.
 * + When an order is canceled and `effectiveAt` is set to `IMMEDIATELY`.
 */
export const onOrderEnded: BuildEventDefinition<typeof publicOnOrderEnded> &
  typeof publicOnOrderEnded = createEventModule(publicOnOrderEnded);
/**
 * Triggered when an offline order is marked as paid.
 */
export const onOrderMarkedAsPaid: BuildEventDefinition<
  typeof publicOnOrderMarkedAsPaid
> &
  typeof publicOnOrderMarkedAsPaid = createEventModule(
  publicOnOrderMarkedAsPaid
);
/**
 * Triggered when an order is paused.
 */
export const onOrderPaused: BuildEventDefinition<typeof publicOnOrderPaused> &
  typeof publicOnOrderPaused = createEventModule(publicOnOrderPaused);
/**
 * Triggered for any of the following purchase events:
 * + Order is paid in full.
 * + At least 1 order cycle payment is paid for.
 * + Offline order is created, even if not yet marked as paid.
 * + Free order is created.
 */
export const onOrderPurchased: BuildEventDefinition<
  typeof publicOnOrderPurchased
> &
  typeof publicOnOrderPurchased = createEventModule(publicOnOrderPurchased);
/**
 * Triggered when a paused order is resumed.
 */
export const onOrderResumed: BuildEventDefinition<typeof publicOnOrderResumed> &
  typeof publicOnOrderResumed = createEventModule(publicOnOrderResumed);
/**
 * Triggered when an order reaches its `startDate`. Applies to both purchased and free orders.
 */
export const onOrderStarted: BuildEventDefinition<typeof publicOnOrderStarted> &
  typeof publicOnOrderStarted = createEventModule(publicOnOrderStarted);

export {
  PeriodUnit,
  OrderType,
  OrderMethod,
  OrderStatus,
  CancellationCause,
  CancellationEffectiveAt,
  PaymentStatus,
  Status,
  Set,
  SortOrder,
  WebhookIdentityType,
  ReasonNotSuspendable,
} from './pricing-plans-v2-order-orders.universal.js';
export {
  Order,
  Buyer,
  PriceDetails,
  PriceDetailsPricingModelOneOf,
  Tax,
  Recurrence,
  Duration,
  Coupon,
  PricingDetails,
  PricingDetailsPricingModelOneOf,
  SpannedPrice,
  PriceDuration,
  Price,
  Fee,
  Cancellation,
  PausePeriod,
  CurrentCycle,
  OrderCycle,
  FormData,
  DomainEvent,
  DomainEventBodyOneOf,
  EntityCreatedEvent,
  RestoreInfo,
  EntityUpdatedEvent,
  EntityDeletedEvent,
  ActionEvent,
  Empty,
  MemberGetOrderRequest,
  MemberGetOrderResponse,
  MemberListOrdersRequest,
  Sorting,
  MemberListOrdersResponse,
  PagingMetadataV2,
  Cursors,
  QueryOrdersRequest,
  QueryV2,
  QueryV2PagingMethodOneOf,
  Paging,
  CursorPaging,
  QueryOrdersResponse,
  RequestCancellationRequest,
  RequestCancellationResponse,
  OrderCanceled,
  MessageEnvelope,
  IdentificationData,
  IdentificationDataIdOneOf,
  AccountInfo,
  CreateOnlineOrderRequest,
  OnBehalf,
  CreateOnlineOrderResponse,
  CouponsError,
  CreateGuestOnlineOrderRequest,
  Captcha,
  Guest,
  CreateGuestOnlineOrderResponse,
  CreateOfflineOrderRequest,
  CreateOfflineOrderResponse,
  CreateExternalOrderRequest,
  CreateExternalOrderResponse,
  GetOnlineOrderPreviewRequest,
  GetOnlineOrderPreviewResponse,
  GetGuestOnlineOrderPreviewRequest,
  GetGuestOnlineOrderPreviewResponse,
  GetOfflineOrderPreviewRequest,
  GetOfflineOrderPreviewResponse,
  GetPricePreviewRequest,
  GetPricePreviewResponse,
  ChangeStartDateRequest,
  ChangeStartDateResponse,
  OrderStartDateChanged,
  ApplyCouponRequest,
  ApplyCouponResponse,
  SetSubmissionRequest,
  SetSubmissionResponse,
  OrderPurchased,
  OrderStarted,
  OrderCycleStarted,
  OrderAutoRenewCanceled,
  OrderEnded,
  GetOrderRequest,
  GetOrderResponse,
  ListOrdersRequest,
  ListOrdersResponse,
  OrdersQueryOrdersRequest,
  OrdersQueryOrdersResponse,
  GetOrdersStatsRequest,
  GetOrdersStatsResponse,
  GetAvailableOrderActionsRequest,
  GetAvailableOrderActionsResponse,
  PostponeEndDateRequest,
  PostponeEndDateResponse,
  OrderEndDatePostponed,
  CancelOrderRequest,
  CancelOrderResponse,
  MarkAsPaidRequest,
  MarkAsPaidResponse,
  OrderMarkedAsPaid,
  PauseOrderRequest,
  PauseOrderResponse,
  OrderPaused,
  BulkPauseOrderRequest,
  BulkPauseOrderResponse,
  BulkOrderResult,
  ItemMetadata,
  ApplicationError,
  BulkActionMetadata,
  ResumeOrderRequest,
  ResumeOrderResponse,
  OrderResumed,
  BulkResumeOrderRequest,
  BulkResumeOrderResponse,
  BaseEventMetadata,
  EventMetadata,
  AccountInfoMetadata,
  OrderCanceledEnvelope,
  OrderCreatedEnvelope,
  OrderStartDateChangedEnvelope,
  OrderUpdatedEnvelope,
  OrderAutoRenewCanceledEnvelope,
  OrderCycleStartedEnvelope,
  OrderEndDatePostponedEnvelope,
  OrderEndedEnvelope,
  OrderMarkedAsPaidEnvelope,
  OrderPausedEnvelope,
  OrderPurchasedEnvelope,
  OrderResumedEnvelope,
  OrderStartedEnvelope,
  MemberGetOrderOptions,
  MemberListOrdersOptions,
  CreateOnlineOrderOptions,
  CreateOfflineOrderOptions,
  GetOnlineOrderPreviewOptions,
  GetOfflineOrderPreviewOptions,
  GetPricePreviewOptions,
  ManagementGetOrderOptions,
  ManagementListOrdersOptions,
} from './pricing-plans-v2-order-orders.universal.js';
export {
  PeriodUnitWithLiterals,
  OrderTypeWithLiterals,
  OrderMethodWithLiterals,
  OrderStatusWithLiterals,
  CancellationCauseWithLiterals,
  CancellationEffectiveAtWithLiterals,
  PaymentStatusWithLiterals,
  StatusWithLiterals,
  SetWithLiterals,
  SortOrderWithLiterals,
  WebhookIdentityTypeWithLiterals,
  ReasonNotSuspendableWithLiterals,
  CreateOnlineOrderApplicationErrors,
  CreateOnlineOrderValidationErrors,
  CreateOfflineOrderApplicationErrors,
  CreateOfflineOrderValidationErrors,
  GetOnlineOrderPreviewApplicationErrors,
  GetOnlineOrderPreviewValidationErrors,
  GetOfflineOrderPreviewApplicationErrors,
  GetOfflineOrderPreviewValidationErrors,
  GetPricePreviewApplicationErrors,
} from './pricing-plans-v2-order-orders.universal.js';
